import { create } from "zustand";
import { CALL_TYPE } from "~/constants/call";

export type CallEndReason =
  | "normal"
  | "partnerHangup"
  | "pointShortage"
  | "networkError"
  | "timeout"
  | "declined"
  | "streamConnectFailed";

export type CallPhase =
  | { type: "idle" }
  | { type: "insufficientPoint" }
  | { type: "outgoingConnecting" }
  | { type: "incomingRinging" }
  | { type: "inCall" }
  // 発信時に相手が通話中（API code 85）だったことを表す。モーダルで通知する
  | { type: "partnerBusy" }
  | { type: "ended"; reason: CallEndReason }
  | { type: "error"; message: string };

type CallType = typeof CALL_TYPE.VIDEO | typeof CALL_TYPE.VOICE;

type PendingOutgoing = {
  partnerId: string;
  partnerName: string;
  partnerAvatarUrl?: string | null;
  partnerAge?: number | null;
};

type IncomingCallInfo = {
  appId: string;
  rtcChannelToken: string;
  rtmChannelToken: string | null;
  channelId: string;
};

type CallState = {
  phase: CallPhase;
  callType: CallType;
  channelName: string | null;
  agoraAppId: string | null;
  partnerId: string | null;
  partnerName: string | null;
  partnerAvatarUrl: string | null;
  partnerAge: number | null;
  partnerLanguage: string | null;
  remoteUid: number;
  isMuted: boolean;
  isCameraOff: boolean;
  isSpeakerOn: boolean;
  callDurationSec: number;
  callStartedAt: Date | null;
  pendingOutgoing: PendingOutgoing | null;
  incomingCallInfo: IncomingCallInfo | null;
  currentPoint: number | null;
  remoteMediaReceived: boolean;
};

type PartnerInfo = {
  partnerId: string;
  partnerName: string;
  partnerAvatarUrl?: string | null;
  partnerAge?: number | null;
  partnerLanguage?: string | null;
};

type CallActions = {
  startCall: (
    channelName: string,
    partner: PartnerInfo,
    callType?: CallType,
  ) => void;
  receiveCall: (
    channelName: string,
    partner: PartnerInfo,
    callType?: CallType,
  ) => void;
  connectCall: () => void;
  endCall: (reason: CallEndReason) => void;
  setPartnerBusy: () => void;
  rejectCall: () => void;
  reset: () => void;
  toggleMute: () => void;
  toggleCamera: () => void;
  toggleSpeaker: () => void;
  incrementDuration: () => void;
  setPhase: (phase: CallPhase) => void;
  setPendingOutgoing: (pending: PendingOutgoing | null) => void;
  setIncomingCallInfo: (info: IncomingCallInfo | null) => void;
  setCurrentPoint: (point: number | null) => void;
  setRemoteMediaReceived: (received: boolean) => void;
};

const initialState: CallState = {
  phase: { type: "idle" },
  callType: CALL_TYPE.VIDEO,
  channelName: null,
  agoraAppId: null,
  partnerId: null,
  partnerName: null,
  partnerAvatarUrl: null,
  partnerAge: null,
  partnerLanguage: null,
  remoteUid: 0,
  isMuted: false,
  isCameraOff: false,
  isSpeakerOn: true,
  callDurationSec: 0,
  callStartedAt: null,
  pendingOutgoing: null,
  incomingCallInfo: null,
  currentPoint: null,
  remoteMediaReceived: false,
};

export const useCallStore = create<CallState & CallActions>()((set) => ({
  ...initialState,
  startCall: (channelName, partner, callType = CALL_TYPE.VIDEO) =>
    set({
      phase: { type: "outgoingConnecting" },
      callType,
      channelName,
      partnerId: partner.partnerId,
      partnerName: partner.partnerName,
      partnerAvatarUrl: partner.partnerAvatarUrl ?? null,
      partnerAge: partner.partnerAge ?? null,
      partnerLanguage: partner.partnerLanguage ?? null,
    }),
  receiveCall: (channelName, partner, callType = CALL_TYPE.VIDEO) =>
    set({
      phase: { type: "incomingRinging" },
      callType,
      channelName,
      partnerId: partner.partnerId,
      partnerName: partner.partnerName,
      partnerAvatarUrl: partner.partnerAvatarUrl ?? null,
      partnerAge: partner.partnerAge ?? null,
      partnerLanguage: partner.partnerLanguage ?? null,
    }),
  connectCall: () =>
    set({
      phase: { type: "inCall" },
      callStartedAt: new Date(),
    }),
  endCall: (reason) =>
    set({
      phase: { type: "ended", reason },
    }),
  setPartnerBusy: () => set({ phase: { type: "partnerBusy" } }),
  rejectCall: () => set(initialState),
  reset: () => set(initialState),
  toggleMute: () => set((state) => ({ isMuted: !state.isMuted })),
  toggleCamera: () => set((state) => ({ isCameraOff: !state.isCameraOff })),
  toggleSpeaker: () => set((state) => ({ isSpeakerOn: !state.isSpeakerOn })),
  incrementDuration: () =>
    set((state) => ({ callDurationSec: state.callDurationSec + 1 })),
  setPhase: (phase) => set({ phase }),
  setPendingOutgoing: (pending) => set({ pendingOutgoing: pending }),
  setIncomingCallInfo: (info) => set({ incomingCallInfo: info }),
  setCurrentPoint: (point) => set({ currentPoint: point }),
  setRemoteMediaReceived: (received) => set({ remoteMediaReceived: received }),
}));
